import { createHash, generateKeyPairSync, sign as cryptoSign, verify as cryptoVerify, createPrivateKey, createPublicKey, type KeyObject } from 'node:crypto'
import { mkdir, readFile, writeFile } from 'node:fs/promises'
import { join } from 'node:path'
import { keysDir } from '../config.js'

export interface SigningKey {
  kid: string
  algorithm: 'Ed25519'
  /** Base64 of the raw 32-byte Ed25519 public key (the format the registry stores). */
  publicKey: string
  /** PKCS#8 PEM private key. */
  privateKeyPem: string
  createdAt: string
}

/** kid = first 16 hex chars of sha256(raw public key), lowercase — must match the registry. */
export function computeKid(publicKeyBase64: string): string {
  return createHash('sha256').update(Buffer.from(publicKeyBase64, 'base64')).digest('hex').slice(0, 16)
}

export function generateSigningKey(): SigningKey {
  const { publicKey, privateKey } = generateKeyPairSync('ed25519')
  const rawPublic = rawPublicKeyBytes(publicKey)
  const publicKeyBase64 = rawPublic.toString('base64')
  return {
    kid: computeKid(publicKeyBase64),
    algorithm: 'Ed25519',
    publicKey: publicKeyBase64,
    privateKeyPem: privateKey.export({ type: 'pkcs8', format: 'pem' }).toString(),
    createdAt: new Date().toISOString(),
  }
}

export function signEd25519(privateKeyPem: string, message: Buffer): Buffer {
  return cryptoSign(null, message, createPrivateKey(privateKeyPem))
}

export function verifyEd25519(publicKeyBase64: string, message: Buffer, signature: Buffer): boolean {
  const jwk = { kty: 'OKP', crv: 'Ed25519', x: Buffer.from(publicKeyBase64, 'base64').toString('base64url') }
  const key = createPublicKey({ key: jwk, format: 'jwk' })
  return cryptoVerify(null, message, key, signature)
}

function rawPublicKeyBytes(publicKey: KeyObject): Buffer {
  const jwk = publicKey.export({ format: 'jwk' }) as { x?: string }
  if (!jwk.x) throw new Error('Failed to export Ed25519 public key.')
  return Buffer.from(jwk.x, 'base64url')
}

export function keyPath(name: string): string {
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name)) {
    throw new Error(`Invalid key name '${name}' — use lowercase letters, digits and hyphens.`)
  }
  return join(keysDir(), `${name}.json`)
}

export async function saveKey(name: string, key: SigningKey): Promise<string> {
  await mkdir(keysDir(), { recursive: true, mode: 0o700 })
  const path = keyPath(name)
  await writeFile(path, JSON.stringify(key, null, 2) + '\n', { mode: 0o600, flag: 'wx' })
  return path
}

export async function loadKey(name: string): Promise<SigningKey> {
  try {
    return JSON.parse(await readFile(keyPath(name), 'utf8')) as SigningKey
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
      throw new Error(`Signing key '${name}' not found. Generate one with \`verentis keygen --name ${name}\`.`)
    }
    throw error
  }
}
